import { connect } from 'react-redux'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { rem } from 'polished'
import { replace } from 'connected-react-router'
import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import styled from 'styled-components'

import { BANNER_LOCATION_TOURS_AND_CRUISES } from 'constants/bannerLocations'
import { fetchBanners } from 'actions/ContentActions'
import { fetchTourSearchFacets } from 'actions/OfferActions'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import { getPlacement } from 'lib/url/campaignUtils'
import { LUXURY_ESCAPES } from 'constants/brands'
import { OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2 } from 'constants/offer'
import BodyText from 'LuxKit/Typography/BodyText'
import config from 'constants/config'
import HeadData from 'components/Common/HeadData'
import Heading from 'LuxKit/Typography/Heading'
import LayoutContainer from 'components/Common/LayoutContainer'
import Leaderboard from 'components/Common/Banner/Leaderboard'
import TextLink from 'LuxKit/TextLink'
import memoize from 'lib/memoize/memoize'
import PartnershipBanner from 'components/Common/Vendors/Partnership/PartnershipBanner'
import Pane from 'components/Common/Pane'
import TourV2SearchControls from 'components/Search/TourV2/TourV2SearchControls'
import {
  getTourGroupTypeSearchMap,
  useGlobalToursSearchContext,
  useGlobalToursSearchResultsTracking,
} from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalToursSearch'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import { GLOBAL_SEARCH_INITIAL_STATE } from 'contexts/GlobalSearch/GlobalSearchState'
import {
  TOURS_OFFER_LIST_EXCLUDED_PARTNERSHIP_CODES,
  TOUR_OFFER_TAG_LIST,
  TOUR_ASIA_TAKEOVER_SHOW_SEARCH_BANNER_PLACE_IDS,
  TOUR_ASIA_TAKEOVER_SEARCH_PLACE_IDS,
} from 'constants/tours'
import { mediaQueryUp } from 'components/utils/breakpoint'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import useQueryParams from 'hooks/useQueryParams'
import OfferList, { OfferListV2Item } from 'components/OfferList/OfferList'
import GeoContext from 'contexts/geoContext'
import useOfferList from 'hooks/Offers/useOfferList'
import { OfferListEventHandler, OfferListEvents, OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import tourV2VerticalNameSelector from 'selectors/TourV2/tourV2VerticalNameSelector'
import { tourV2ShouldShowFilterToggles } from 'selectors/TourV2/TourV2SearchSelectors'
import { ANYWHERE_PLACE_ID, DATE_SEARCH_OPTION_IDS } from 'constants/search'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import TourSearchSortHeader from './TourSearchSortHeader'
import TourSearchPageFilters, { TourSearchPageFilterSubmit } from 'components/Tours/TourSearchPageFilters'
import { deleteSearchParams, parseSearchString, setSearchParamValue } from 'lib/url/searchUrlUtils'
import LineFilterAltIcon from 'LuxKit/Icons/line/LineFilterAltIcon'
import useToggle from 'hooks/useToggle'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import TextButton from 'LuxKit/Button/TextButton'
import { tourGroupTypeIds } from 'components/Search/TourV2/TourV2SearchGroupSelect'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TourSearchFilterContent from 'components/Tours/TourSearchFilterContent'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { formToObject } from 'lib/forms/formToObject'
import { SORT_OPTION_RECOMMENDED, tourSortByTypes } from 'constants/offerListFilters'
import { getListName } from 'analytics/enhanced-ecommerce'
import getObjectKey from 'lib/object/getObjectKey'
import getOfferListKey from 'lib/offer/offerListKey'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import debounce from 'debounce-promise'
import cn from 'clsx'
import { useInitialTourFacets } from '../../../../hooks/TourV2/useInitialTourFacets'
import RequestAgentCallbackModal from 'supportAssistant/components/RequestAgentCallback/Modals/RequestAgentCallbackModal'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import ModalContext from 'contexts/ModalContext'
import LineIncomingCallIcon from 'LuxKit/Icons/line/LineIncomingCallIcon'
import { searchEvent } from 'analytics/snowplow/events'
import * as Analytics from 'analytics/analytics'
import TourSpendEarnPageBanner from 'components/Tours/TourSpendEarnPageBanner'
import { isTourPromoSpendEarnEnabled } from 'selectors/TourV2/toursSelectors'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import SiteTakeoverSearchBanner from 'takeovers/utils/components/SiteTakeoverSearchBanner'
import { useShouldShowBanner } from 'takeovers/utils/hooks/useShouldShowBanner'
import TourPriceToggle from 'components/Tours/TourPriceToggle'
import Group from 'components/utils/Group'

const ANALYTICS_TOUR_SEARCH_TAG = 'TS'

const OfferListWrapper = styled(LayoutContainer)`
  padding-bottom: ${rem(16)};
  padding-top: ${rem(16)};
  padding-inline: 0;

  ${mediaQueryUp.tablet} {
    padding-inline: ${rem(16)};
  }

  ${mediaQueryUp.desktop} {
    && {
      max-width: ${rem(1352)};
    }
  }

  > * + * {
    margin-top: ${rem(16)};
  }
`

const NoOffersMessage = styled.div`
  padding: ${rem(24)} 0;

  > * + * {
    margin-top: ${rem(8)};
  }
`

const HeaderPane = styled(Pane)`
  box-shadow: ${props => props.theme.shadow.flat.small};
  padding: ${rem(12)} 0;
`

const StyledLayoutContainer = styled(LayoutContainer)`
  > * + * {
    margin-top: ${rem(16)};
  }
`

const FilterToggleControls = styled.div`
  align-items: center;
  display: grid;
  gap: ${rem(8)};
  grid-auto-flow: column;
  justify-content: left;
`

const ListWrapper = styled.div`
  > * + * {
    margin-top: ${rem(8)};
  }
`

const HeadingContainer = styled.div`
  display: flex;
  gap: ${rem(8)};
  align-items: center;
  justify-content: space-between;
`

const PageContent = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  padding: ${rem(20)} 0;

  ${mediaQueryUp.tablet} {
    padding: ${rem(16)} 0;
    gap: ${rem(32)};
    grid-template-columns: ${rem(250)} 1fr;
  }
`

const SuggestionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(20)};

  &.margin-top {
    margin-top: ${rem(40)};
  }
`

interface MappedStateProps {
  availableFilters?: App.OfferListFilterOptions
  banners: Array<App.Banner>
  filters: App.OfferListFilters
  toursVerticalName: string
  shouldShowFilterToggles: boolean
  tourFacetsState?: App.StatefulData<App.Tours.TourSearchFacets>
  windowSearch: string
}

interface MappedDispatchProps {
  fetchBanners: Utils.ActionDispatcher<typeof fetchBanners>
}

interface Props {}

const getBannerPlacements = memoize(
  (banners: Array<App.Banner>) => ({
    top: getPlacement(banners, 'top'),
  }),
)

const filterToArray = (value?: string | Array<string>): Array<string> => {
  if (value) {
    return Array.isArray(value) ? value : [value]
  }
  return []
}

function TourV2SearchPage(props: Props & MappedStateProps & MappedDispatchProps) {
  const {
    banners,
    fetchBanners,
    filters,
    toursVerticalName,
    tourFacetsState,
    windowSearch,
  } = props

  const isMobileScreen = useScreenSizeOnly('mobile')

  const { currentRegionCode, partnership } = useContext(GeoContext)
  const queryParams = useQueryParams()
  const { globalToursSearchDispatch, globalToursSearchState } = useGlobalToursSearchContext({
    ...GLOBAL_SEARCH_INITIAL_STATE,
    dateSearchOptionId: DATE_SEARCH_OPTION_IDS.FLEXIBLE,
  }, queryParams)
  const { searchItem, tourGroupTypes } = globalToursSearchState

  const promoSpendEarnEnabled = useAppSelector(state => isTourPromoSpendEarnEnabled(state))

  const dispatch = useAppDispatch()

  const currentSiteTakeover = useCurrentSiteTakeover({ vertical: 'tours' })
  const shouldShowBanner = useShouldShowBanner({ location: 'search', vertical: 'tours' })

  const callbackType: App.GenesysAgentCallbackType = 'TOURS'
  const params = useMemo(() => parseSearchString(windowSearch), [windowSearch])
  const location = params.destinationName ? params.destinationName.replace(/\+/g, ' ') : ''

  const callbacksFeatureEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.toursRequestAgentCallbackEnabled)

  const showModal = useContext(ModalContext)

  const tourGroupTypesSearch = getTourGroupTypeSearchMap(tourGroupTypes)

  const listFilters = useMemo((): App.OfferListFilters => ({
    ...filters,
    ...tourGroupTypesSearch,
    offerTypes: [OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2],
    showEnrichedResult: true,
  }), [filters, tourGroupTypesSearch])

  useEffect(() => {
    dispatch(fetchTourSearchFacets(listFilters))
    // populate initial facets on reload for sliders
    dispatch(fetchTourSearchFacets({ destinationId: listFilters.destinationId ?? '', offerTypes: [OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2] }))
  }, [listFilters, dispatch])

  useEffect(() => {
    fetchBanners(BANNER_LOCATION_TOURS_AND_CRUISES)
  }, [fetchBanners])

  const offerList = useOfferList(listFilters)
  const zeroSearchResults = offerList.offerIds.length === 0 && !offerList.fetching
  const anytimeListFilters = useMemo(() => ({
    ...listFilters,
    flexibleMonths: '',
    flexibleNights: '',
  }), [listFilters])
  const anytimeOfferList = useOfferList(anytimeListFilters)
  const isAnywhereSearch = listFilters.destinationId === ANYWHERE_PLACE_ID
  const isAnytimeSearch = !(listFilters.flexibleMonths || listFilters.flexibleNights)
  const showAnytimeOffers = zeroSearchResults && isAnytimeSearch && !anytimeOfferList.fetching && anytimeOfferList.offerIds.length > 0

  const anywhereListFilters = useMemo<App.OfferListFilters>(() => ({
    ...listFilters,
    destinationId: ANYWHERE_PLACE_ID,
  }), [listFilters])

  const anywhereAnytimeFilter = useMemo<App.OfferListFilters>(() => ({
    ...listFilters,
    destinationId: ANYWHERE_PLACE_ID,
    flexibleMonths: '',
    flexibleNights: '',
    priceGte: undefined,
    priceLte: undefined,
    tourLengthGte: undefined,
    tourLengthLte: undefined,
  }), [listFilters])
  const anywhereOfferList = useOfferList(anywhereListFilters, { disabled: !zeroSearchResults && !showAnytimeOffers })
  const showAnywhereOffers = zeroSearchResults && !showAnytimeOffers && anywhereOfferList.offerIds.length > 0 && !anywhereOfferList.fetching

  const tracking = useOfferListTracking(
    getListName('OfferList', ANALYTICS_TOUR_SEARCH_TAG, 'Search Page Offer List'),
    { key: getOfferListKey(listFilters) },
  )

  const onListEvent = useCallback<OfferListEventHandler>((dispatchAction) => {
    if (dispatchAction.type === OfferListEvents.listLoad) {
      Analytics.trackEvent(searchEvent('offer-list-load', 'tours-search-page', {
        filters,
        searchItem,
        checkIn: filters.checkIn,
        checkOut: filters.checkOut,
        vertical: 'tours',
        occupancies: filters.rooms,
      }))
    }
  }, [filters, searchItem])

  const onTourFilterApply = useCallback((values: TourSearchPageFilterSubmit) => {
    let search = windowSearch
    const tourStyles = filterToArray(values.tourStyles)
    const destinations = filterToArray(values.destinations)
    const operators = filterToArray(values.operators)
    const categories = filterToArray(values.categories)
    const groupTypes = filterToArray(values.groupTypes)
    const offerTags = filterToArray(values.offerTags)
    const productTypes = filterToArray(values.productTypes)
    const luxPlusFeatures = filterToArray(values.luxPlusFeatures)
    const agentHubFeatures = filterToArray(values.agentHubFeatures)
    const campaigns = filterToArray(values.campaigns)
    const priceLte = values.price.end
    const priceGte = values.price.start
    const tourLengthLte = values.duration.end
    const tourLengthGte = values.duration.start
    const sortBy = values.sortBy

    if (tourStyles.length) {
      search = setSearchParamValue(search, 'tourStyles', tourStyles.join(','))
    } else {
      search = deleteSearchParams(search, 'tourStyles')
    }

    search = setSearchParamValue(search, 'destinations', destinations.join(','))
    search = setSearchParamValue(search, 'operators', operators.join(','))
    if (categories.length) {
      search = setSearchParamValue(search, 'categories', categories.join(','))
    } else {
      search = deleteSearchParams(search, 'categories')
    }
    search = setSearchParamValue(search, 'productTypes', productTypes.join(','))
    search = setSearchParamValue(search, 'priceLte', priceLte)
    search = setSearchParamValue(search, 'priceGte', priceGte)
    search = setSearchParamValue(search, 'tourLengthLte', tourLengthLte)
    search = setSearchParamValue(search, 'tourLengthGte', tourLengthGte)
    search = setSearchParamValue(search, 'luxPlusFeatures', luxPlusFeatures)
    search = setSearchParamValue(search, 'agentHubFeatures', agentHubFeatures)
    search = setSearchParamValue(search, 'campaigns', campaigns.join(','))
    if (productTypes.length === 0 || !productTypes.every(Boolean)) {
      search = deleteSearchParams(search, 'productTypes')
    }

    // clean group type filter
    search = deleteSearchParams(search, ...tourGroupTypeIds)

    groupTypes.forEach(groupType => {
      search = setSearchParamValue(search, groupType, 'true')
    })

    // clean offer tag (onSale) filter
    search = deleteSearchParams(search, ...TOUR_OFFER_TAG_LIST)

    offerTags.forEach(offerTag => {
      search = setSearchParamValue(search, offerTag, 'true')
    })

    if (sortBy === SORT_OPTION_RECOMMENDED.value) {
      search = deleteSearchParams(search, 'sortBy')
    } else if (sortBy) {
      search = setSearchParamValue(search, 'sortBy', sortBy)
    }
    dispatch(replace({ search }))
  }, [dispatch, windowSearch])

  const onSortChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    let nextSearch
    if (e.currentTarget.value === SORT_OPTION_RECOMMENDED.value) {
      nextSearch = deleteSearchParams(windowSearch, 'sortBy')
    } else {
      nextSearch = setSearchParamValue(windowSearch, 'sortBy', e.currentTarget.value)
    }
    dispatch(replace({ search: nextSearch }))
  }, [dispatch, windowSearch])

  const bannerPlacements = getBannerPlacements(banners)

  const suggestionListFilters: { filters: App.OfferListFilters, heading: string } | undefined = useMemo(() => {
    if (anytimeOfferList.fetching || anywhereOfferList.fetching) {
      return
    }

    if (anytimeOfferList.offerIds.length && (isAnywhereSearch || (!isAnytimeSearch && !isAnywhereSearch))) {
      return {
        filters: anytimeListFilters,
        heading: 'Other tours available outside of your dates',
      }
    }

    if (anywhereOfferList.offerIds.length && (isAnytimeSearch || (!isAnytimeSearch && !isAnywhereSearch))) {
      return {
        filters: anywhereListFilters,
        heading: `Other tours available outside of ${searchItem?.format.mainText}`,
      }
    }

    const heading = (() => {
      if (isAnytimeSearch && !isAnywhereSearch) {
        return `Other tours available outside of ${searchItem?.format.mainText}`
      }

      return 'Other tours available outside of your dates'
    })()

    return {
      filters: anywhereAnytimeFilter,
      heading,
    }
  }, [anytimeListFilters, anytimeOfferList, anywhereAnytimeFilter, anywhereListFilters, anywhereOfferList, isAnytimeSearch, isAnywhereSearch, searchItem?.format.mainText])

  const offerListExtras: Array<OfferListV2Item> = useMemo(() => {
    const totalNumberOfOffers = offerList.offerIds.length

    const handleCallbackClick = () => { showModal(<RequestAgentCallbackModal type={callbackType} filters={filters} location={location} />) }

    const extras: Array<OfferListV2Item> = [
      {
        element: <HeadingContainer>
          <TextLoadingBox typography="heading4" width={140} />
          <TextLoadingBox typography="body-large" width={150} />
        </HeadingContainer>,
        id: 'offers-count-loading',
        position: 'start',
        show: offerList.fetching && !isMobileScreen,
      },
      {
        element: <div>
          <TourSearchSortHeader
            fetching={tourFacetsState?.fetching}
            totalNumberOfOffers={totalNumberOfOffers}
            onChange={onSortChange}
          />
          <FilterToggleControls>
          </FilterToggleControls>
        </div>,
        id: 'filter-and-sort',
        position: 'start',
        show: !!tourFacetsState && !isMobileScreen,
      },
      {
        element: <PartnershipBanner bannerId="types-page" />,
        show: !!partnership && !TOURS_OFFER_LIST_EXCLUDED_PARTNERSHIP_CODES.has(partnership.code),
        id: 'partnership',
        position: 'start',
      },
      {
        element: <Leaderboard banners={bannerPlacements.top} />,
        show: bannerPlacements.top.length > 0,
        id: 'leaderboard',
        position: 'start',
      },
      {
        element: <NoOffersMessage>
          <Heading variant="heading3">Sorry, we couldn’t find any tours or cruises in {isAnywhereSearch ? 'this region' : searchItem?.format.mainText}</Heading>
          {isAnywhereSearch ? <BodyText variant="large">Try adjusting your search or filters</BodyText> :
              <BodyText variant="large">Try a different location or explore <TextLink to="/tours">all offers</TextLink></BodyText>}
          <BodyText variant="large">Need help? Contact your 24/7 booking concierge for exclusive packages not currently available online:</BodyText>
          {callbacksFeatureEnabled && <TextButton fit="mobile-full-width" startIcon={<LineIncomingCallIcon />} kind="secondary" onClick={handleCallbackClick}>Request a callback</TextButton>}
        </NoOffersMessage>,
        id: 'no-offers-message',
        position: 'start',
        show: showAnytimeOffers || showAnywhereOffers ? false : 'hasNoOffers',
      },
      {
        element: <div>
          <NoOffersMessage>
            <Heading variant="heading3">Sorry, we couldn’t find any tours or cruises in {searchItem?.format.mainText} for those dates</Heading>
            <BodyText variant="large">Try a different location or explore <TextLink to="/tours">all offers</TextLink></BodyText>
            <BodyText variant="large">Need help? Contact your 24/7 booking concierge for exclusive packages not currently available online:</BodyText>
            {callbacksFeatureEnabled && <TextButton fit="mobile-full-width" startIcon={<LineIncomingCallIcon />} kind="secondary" onClick={handleCallbackClick}>Request a callback</TextButton>}
          </NoOffersMessage>
        </div>,
        id: 'no-offers-message',
        position: 'start',
        show: showAnywhereOffers,
      },
      {
        element: <div>
          <NoOffersMessage>
            <Heading variant="heading3">Sorry, we couldn’t find any tours or cruises in {searchItem?.format.mainText} for those dates</Heading>
            <BodyText variant="large">Try a different location or explore <TextLink to="/tours">all offers</TextLink></BodyText>
            <BodyText variant="large">Need help? Contact your 24/7 booking concierge for exclusive packages not currently available online:</BodyText>
            {callbacksFeatureEnabled && <TextButton fit="mobile-full-width" startIcon={<LineIncomingCallIcon />}kind="secondary" onClick={handleCallbackClick}>Request a callback</TextButton>}
          </NoOffersMessage>
        </div>,
        id: 'no-offers-message',
        position: 'start',
        show: showAnytimeOffers,
      },
      {
        element: <SuggestionContainer className={cn({ 'margin-top': offerList.offerIds.length })}>
          <Heading variant="heading3">{suggestionListFilters?.heading}</Heading>
          <OfferList
            filters={suggestionListFilters?.filters}
            tileStyle="search"
            excludedIds={offerList.offerIds}
          />
        </SuggestionContainer>,
        id: 'suggested-offers',
        position: 'end',
        show: (!(isAnytimeSearch && isAnywhereSearch) && !!suggestionListFilters),
      },
    ]
    return extras
  }, [offerList.offerIds, offerList.fetching, isMobileScreen, tourFacetsState, onSortChange, partnership, bannerPlacements.top, isAnywhereSearch, searchItem?.format.mainText, callbacksFeatureEnabled, showAnytimeOffers, showAnywhereOffers, suggestionListFilters, isAnytimeSearch, showModal, filters, location])

  const headTitle = config.BRAND === LUXURY_ESCAPES ?
    `Luxury ${toursVerticalName}` :
    `${toursVerticalName}`
  const description = config.BRAND === LUXURY_ESCAPES ?
    'Embark on a life changing tour or cruise. Curated by travel experts to save you time. Featuring luxury accommodation, select dining and exclusive inclusions.' :
    undefined

  useGlobalToursSearchResultsTracking(globalToursSearchState)

  const {
    on: openFilters,
    off: closeFilters,
    value: showFilters,
  } = useToggle()

  const onFilterFormChange = useMemo(() => debounce((values: TourSearchPageFilterSubmit) => {
    onTourFilterApply({
      tourStyles: values.tourStyles,
      destinations: values.destinations,
      operators: values.operators,
      categories: values.categories,
      groupTypes: values.groupTypes,
      offerTags: values.offerTags,
      productTypes: values.productTypes,
      price: {
        start: values.price.start,
        end: values.price.end,
      },
      duration: {
        start: values.duration.start,
        end: values.duration.end,
      },
      luxPlusFeatures: values.luxPlusFeatures,
      agentHubFeatures: values.agentHubFeatures,
      campaigns: values.campaigns,
      sortBy: values.sortBy,
    })
  }, 250), [onTourFilterApply])

  const handleFilterFormChange = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const element = e.currentTarget as HTMLFormElement
    const values = formToObject<TourSearchPageFilterSubmit>(element)
    onFilterFormChange(values)
  }

  const onFilterFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    handleFilterFormChange(e)
    closeFilters()
  }

  const showTakeoverSearchBanner = useMemo<boolean>(() => {
    // Use hook for general banner visibility
    if (!shouldShowBanner) {
      return false
    }

    // Custom logic for 'tourbestasia' takeover
    if (currentSiteTakeover?.id === 'tourbestasia') {
      return (!!filters.destinationId && TOUR_ASIA_TAKEOVER_SHOW_SEARCH_BANNER_PLACE_IDS.includes(filters.destinationId) &&
        ((!!filters.destinations?.length && filters.destinations.some(destination => TOUR_ASIA_TAKEOVER_SEARCH_PLACE_IDS.includes(destination))) ||
          !filters.destinations?.length)
      )
    }

    // For other takeovers, use hook result
    return true
  }, [shouldShowBanner, filters, currentSiteTakeover])

  const initialFacets = useInitialTourFacets(filters.destinationId ?? '', currentRegionCode)

  return <AnalyticsPageContext.Provider value={AnalyticsPage.checkout}>
    <div>
      <HeadData title={`${headTitle} - ${config.title} ${currentRegionCode}`} description={description} />

      {promoSpendEarnEnabled && <TourSpendEarnPageBanner />}
      {showTakeoverSearchBanner && <SiteTakeoverSearchBanner vertical="tours" />}

      <GlobalSearchContextProvider
        state={globalToursSearchState}
        dispatch={globalToursSearchDispatch}
      >
        <HeaderPane>
          <StyledLayoutContainer>
            <div style={{ position: 'relative' }}>
              <TourV2SearchControls editMenuOnMobile />
            </div>
            {isMobileScreen && <Group direction="horizontal" verticalAlign="center" gap={8}>
              <TextButton
                kind="secondary"
                variant="dark"
                startIcon={<LineFilterAltIcon />}
                onClick={openFilters}
                size="medium"
              >
                Sort & filter
              </TextButton>
              <TourPriceToggle />
            </Group>}
          </StyledLayoutContainer>
        </HeaderPane>
      </GlobalSearchContextProvider>

      <TourSearchPageFilters
        open={showFilters}
        onClose={closeFilters}
        onFilterSubmit={onFilterFormSubmit}
        searchFacetsState={tourFacetsState}
        existingFilters={listFilters}
        sortOptions={tourSortByTypes}
        initialFacets={initialFacets?.data}
      />

      <OfferListWrapper>
        <PageContent>
          <CSSBreakpoint min="tablet">
            <form onChange={handleFilterFormChange}>
              <TourSearchFilterContent
                onApply={onTourFilterApply}
                searchFacetsState={tourFacetsState}
                existingFilters={listFilters}
                initialFacets={initialFacets?.data}
              />
            </form>
          </CSSBreakpoint>
          <VerticalSpacer gap={16}>
            <ListWrapper>
              <OfferListEventsProvider tracking={tracking} onListEvent={onListEvent}>
                <OfferList
                  filters={listFilters}
                  tileStyle="search"
                  interstitials={offerListExtras}
                />
              </OfferListEventsProvider>
            </ListWrapper>
          </VerticalSpacer>
        </PageContent>
      </OfferListWrapper>
    </div>
  </AnalyticsPageContext.Provider>
}

export default connect<MappedStateProps, MappedDispatchProps, Props, App.State>((state) => {
  const filters = getUrlOfferListFilters(state)
  const region = state.geo.currentRegionCode
  const facetKey = getObjectKey({
    placeId: filters.destinationId ?? '',
    region,
    category: filters.categories?.[0] ?? null,
    luxPlusExclusive: filters.luxPlusFeatures,
    priceGte: filters.priceGte ? parseFloat(filters.priceGte) : undefined,
    priceLte: filters.priceLte ? parseFloat(filters.priceLte) : undefined,
    onSale: filters.onSale,
    flashOffer: filters.flashOffer,
    tourLengthGte: filters.tourLengthGte ? parseFloat(filters.tourLengthGte) : undefined,
    tourLengthLte: filters.tourLengthLte ? parseFloat(filters.tourLengthLte) : undefined,
  })
  const listFilterKey = getOfferListKey({
    ...filters,
    offerTypes: [OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2],
  },
  )
  return {
    availableFilters: state.offer.offerListFilterOptions[listFilterKey],
    banners: state.content.banners.toursAndCruises,
    tourFacetsState: state.tour.tourFacets[facetKey],
    filters,
    toursVerticalName: tourV2VerticalNameSelector(state),
    shouldShowFilterToggles: tourV2ShouldShowFilterToggles(state),
    windowSearch: state.router.location.search,
  }
}, {
  fetchBanners,
})(TourV2SearchPage)
